import { Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";

// Only eagerly load the homepage for fast initial paint
import Index from "@/pages/Index";

// Lazy load all other routes for code splitting
const Shop = lazy(() => import("@/pages/Shop"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Cart = lazy(() => import("@/pages/Cart"));
const Auth = lazy(() => import("@/pages/Auth"));
const Account = lazy(() => import("@/pages/Account"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Admin = lazy(() => import("@/pages/Admin"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Shipping = lazy(() => import("@/pages/Shipping"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Documentation = lazy(() => import("@/pages/Documentation"));
const Quality = lazy(() => import("@/pages/Quality"));
const BulkOrders = lazy(() => import("@/pages/BulkOrders"));
const Support = lazy(() => import("@/pages/Support"));
const PillsCategory = lazy(() => import("@/pages/PillsCategory"));
const ResearchChemicalsCategory = lazy(() => import("@/pages/ResearchChemicalsCategory"));
const CannabisCategory = lazy(() => import("@/pages/CannabisCategory"));
const MercuryCategory = lazy(() => import("@/pages/MercuryCategory"));
const NootropicsCategory = lazy(() => import("@/pages/NootropicsCategory"));
const PeptidesCategory = lazy(() => import("@/pages/PeptidesCategory"));
const IndustrialChemicalsCategory = lazy(() => import("@/pages/IndustrialChemicalsCategory"));
const LabSuppliesCategory = lazy(() => import("@/pages/LabSuppliesCategory"));
const MoistureAnalyzers = lazy(() => import("@/pages/MoistureAnalyzers"));
const BalancesScales = lazy(() => import("@/pages/BalancesScales"));
const FatAnalyzers = lazy(() => import("@/pages/FatAnalyzers"));
const About = lazy(() => import("@/pages/About"));
const Filters = lazy(() => import("@/pages/Filters"));
const Contact = lazy(() => import("@/pages/Contact"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const OrderConfirmation = lazy(() => import("@/pages/OrderConfirmation"));
const OrderDetails = lazy(() => import("@/pages/OrderDetails"));
const OAuthConsent = lazy(() => import("@/pages/OAuthConsent"));

const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
  </div>
);

const AnimatedRoutes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/product/:slug" element={<ProductDetail />} />
        {/* Legacy slug redirects (301-style client redirects to fix soft 404s) */}
        <Route path="/product/eutylone-bk-ebdb" element={<Navigate to="/product/eutylone" replace />} />
        <Route path="/product/5-apb-benzofuran" element={<Navigate to="/product/5-apb-hydrochloride" replace />} />
        <Route path="/product/broad-spectrum-hemp-extract" element={<Navigate to="/product/full-spectrum-hemp-extract" replace />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/account" element={<Account />} />
        <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/shipping" element={<Shipping />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/documentation" element={<Documentation />} />
        <Route path="/quality" element={<Quality />} />
        <Route path="/bulk-orders" element={<BulkOrders />} />
        <Route path="/support" element={<Support />} />
        <Route path="/pills-painkillers" element={<PillsCategory />} />
        <Route path="/research-chemicals" element={<ResearchChemicalsCategory />} />
        <Route path="/cannabis" element={<CannabisCategory />} />
        <Route path="/liquid-mercury" element={<MercuryCategory />} />
        <Route path="/nootropics" element={<NootropicsCategory />} />
        <Route path="/peptides" element={<PeptidesCategory />} />
        <Route path="/industrial-chemicals" element={<IndustrialChemicalsCategory />} />
        <Route path="/lab-supplies" element={<LabSuppliesCategory />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/order-confirmation" element={<OrderConfirmation />} />
        <Route path="/order/:id" element={<OrderDetails />} />
        <Route path="/moisture-analyzers" element={<MoistureAnalyzers />} />
        <Route path="/balances-scales" element={<BalancesScales />} />
        <Route path="/fat-analyzers" element={<FatAnalyzers />} />
        <Route path="/filters" element={<Filters />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AnimatedRoutes;
